import { useState } from 'react';

import { CrosshairIcon, LocateFixedIcon, SparklesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { popModal } from '@/redux/modals/actions';
import { selectAccountSlug } from '@/redux/session/selectors';
import { AmendPayload } from '@/shared/types/tryIt';

import { AmendIntent } from './AmendIntent';

import styles from '../../LogSections/DetailsSection/DetailsSection.module.scss';

interface Props {
  brainId: string;
  isPlannerIntentOfftopic: boolean;
  intentName?: string;
  plannerIntent?: string;
  possibleMatchedIntents?: { intent: string; confidence: number }[];
  showAddTrainingPhrase?: boolean;
  userMessage?: string;
  amendPayload?: AmendPayload;
  isAmended?: boolean;
}

export const PossibleMatchedIntents = ({
  brainId,
  isPlannerIntentOfftopic,
  intentName,
  plannerIntent,
  possibleMatchedIntents,
  showAddTrainingPhrase,
  userMessage,
  amendPayload,
  isAmended,
}: Props) => {
  const slug = useSelector(selectAccountSlug);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [amended, setAmended] = useState(isAmended);

  const closeModal = () => {
    dispatch(popModal());
  };
  const intent = plannerIntent || intentName;

  const isOfftopic = intent === 'offtopics' || isPlannerIntentOfftopic;

  const filteredMatchedIntents = possibleMatchedIntents?.filter(
    (intent) => intent.intent !== plannerIntent && intent.intent !== intentName
  );

  const matchedPlannerIntent = possibleMatchedIntents?.find((i) => i.intent === intent);

  // Messages that don't match intents like reminder matches
  const noIntentnoOfftopic = !isOfftopic && !intent && !matchedPlannerIntent;

  if (noIntentnoOfftopic) {
    return null;
  }

  return (
    <>
      <div className={styles.title}>
        {isOfftopic ? (
          <>
            <CrosshairIcon size={16} className="text-foreground-muted" />
            <div className="text-sm leading-5 font-normal text-foreground-muted">
              {t('try_it.no_intent_ai_agent')}
            </div>
          </>
        ) : (
          <>
            <div className={styles.iconWrapper}>
              <LocateFixedIcon size={16} className="text-foreground-muted" />
            </div>
            <div className={styles.wrapper}>
              <span className="text-sm leading-5 font-normal text-foreground-muted">
                {t('common.intent')}{' '}
              </span>
              <div className={styles.plannerIntent}>
                <SparklesIcon size={16} className="text-ai" />
                <NavLink
                  to={`/${slug}/ai-agents/${brainId}/workflows/intents/${intent}`}
                  className={styles.link}
                  onClick={closeModal}
                >
                  <span className="text-sm leading-5 font-normal text-foreground">{intent}</span>
                </NavLink>
              </div>{' '}
              <span className="text-sm leading-5 font-normal text-foreground-muted">
                {t('try_it.selected_ai_agent', {
                  0: (matchedPlannerIntent?.confidence * 100).toFixed(0),
                })}
              </span>
            </div>
          </>
        )}
      </div>
      <div className={styles.details}>
        {filteredMatchedIntents?.length > 0 && (
          <Accordion type="single" collapsible className="pt-1 pl-2">
            <AccordionItem value="more" className="border-0">
              <AccordionTrigger className="py-0 pr-2 pl-4 text-sm font-normal text-foreground-muted hover:no-underline">
                {isOfftopic ? t('try_it.considered') : t('try_it.other_considered')}:
              </AccordionTrigger>
              <AccordionContent className="mt-2 p-0">
                <ol>
                  {(isOfftopic ? possibleMatchedIntents : filteredMatchedIntents)?.map((intent) => (
                    <div
                      key={intent.intent}
                      className={cn(
                        'text-sm leading-5 font-normal text-foreground-muted',
                        styles.details__content
                      )}
                    >
                      {t('try_it.possible_match')}
                      {': '}
                      <span className="text-sm leading-5 font-normal">
                        <NavLink
                          to={`/${slug}/ai-agents/${brainId}/workflows/intents/${intent.intent}`}
                          className={styles.link}
                          onClick={closeModal}
                        >
                          {intent?.intent}
                        </NavLink>
                      </span>{' '}
                      {t('try_it.confidence_percentage', {
                        0: (intent?.confidence * 100).toFixed(0),
                      })}
                    </div>
                  ))}
                </ol>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        )}
        {showAddTrainingPhrase && (
          <AmendIntent
            possibleMatchedIntents={possibleMatchedIntents}
            brainId={brainId}
            userMessage={userMessage}
            amendPayload={amendPayload}
            setAmended={setAmended}
            amended={amended}
          />
        )}
      </div>
    </>
  );
};
